import { useState, useEffect, useMemo } from "react";
import { fetchWithAuth } from "@/lib/api";
import type { Asset } from "@/schemas";
import { API_BASE } from "@/config/api";

/**
 * Hook to get a mapping of currency asset IDs to their ISO codes (symbols)
 * This resolves UUIDs like "550e8400-e29b-41d4-a716-446655440001" to "USD"
 */
export function useCurrencyMap(): {
  currencyMap: Map<string, string>;
  resolveCurrency: (currencyIdOrCode?: string) => string;
  loading: boolean;
} {
  const [fiatAssets, setFiatAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFiatAssets = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/assets?type=fiat`);
        if (response.ok) {
          const data = await response.json();
          setFiatAssets(data.results || []);
        }
      } catch (error) {
        console.error("Error fetching fiat assets for currency map:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFiatAssets();
  }, []);

  // Create a map: asset_id -> symbol (e.g., "550e8400-..." -> "USD")
  const currencyMap = useMemo(() => {
    const map = new Map<string, string>();
    fiatAssets.forEach((asset) => {
      if (asset.asset_id && asset.symbol) {
        map.set(asset.asset_id, asset.symbol);
      }
    });
    return map;
  }, [fiatAssets]);

  // Resolve currency: if it's a UUID, return the symbol; otherwise return as-is
  const resolveCurrency = (currencyIdOrCode?: string): string => {
    if (!currencyIdOrCode) return "USD";
    
    // If it's already a valid ISO code (3 uppercase letters), return it
    if (/^[A-Z]{3}$/.test(currencyIdOrCode)) {
      return currencyIdOrCode;
    }
    
    // If it looks like a UUID, try to resolve it
    if (currencyIdOrCode.includes("-") && currencyIdOrCode.length > 30) {
      const resolved = currencyMap.get(currencyIdOrCode);
      if (resolved) {
        return resolved;
      }
      // Fallback to USD if UUID not found
      return "USD";
    }
    
    // Otherwise, assume it's already a code
    return currencyIdOrCode.toUpperCase();
  };

  return { currencyMap, resolveCurrency, loading };
}
